import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { BookOpen, Check, X, RotateCcw, Clock } from 'lucide-react';

// =====================================================================
// PLENARIUM:ATA_RETIFICACAO -- pedidos de retificacao da ata (admin).
//  - na materia da ata, no Tramite: <RetificacoesAta projetoId={...} />
//  - na tela da sessao: <RetificacoesAta /> (so aparece se houver pedidos
//    em atas ainda nao votadas)
// Aceitar: o texto (pode ser ajustado) entra sozinho no campo
// "Retificacao" da pauta da ata. Recusar: o vereador ve a resposta.
// =====================================================================

const horaBR = (d) => (d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const tituloAta = (a) => (a ? (a.ementa || `Ata nº ${a.numero || ''}${a.ano ? '/' + a.ano : ''}`) : '');
const SIT = { PENDENTE: ['Aguardando análise', 'badge-amarelo'], ACEITO: ['Aceito', 'badge-verde'], RECUSADO: ['Recusado', 'badge-vermelho'] };

export default function RetificacoesAta({ projetoId }) {
  const [dados, setDados] = useState(null);
  const [decidir, setDecidir] = useState(null); // { pedido, status, textoAceito, resposta }
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(() => {
    axios.get('/api/atas/admin', { params: projetoId ? { projetoId } : {} })
      .then(r => setDados(r.data || { atas: [] }))
      .catch(() => setDados(d => d || { atas: [], erro: true }));
  }, [projetoId]);

  useEffect(() => { carregar(); }, [carregar]);
  const camaraId = dados && dados.camaraId;
  useEffect(() => {
    if (!camaraId) return undefined;
    const s = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    s.on('connect', () => { s.emit('entrar-sala', `camara-${camaraId}`); carregar(); });
    s.on('atas-atualizado', carregar);
    const voltou = () => { if (document.visibilityState === 'visible') carregar(); };
    document.addEventListener('visibilitychange', voltou);
    return () => { s.disconnect(); document.removeEventListener('visibilitychange', voltou); };
  }, [camaraId, carregar]);

  const salvar = async (pedido, status, extra = {}) => {
    setOcupado(true);
    try {
      await axios.patch(`/api/atas/pedidos/${pedido.id}`, { status, ...extra });
      toast.success(status === 'ACEITO' ? 'Pedido aceito: entrou na retificação da ata.' : status === 'RECUSADO' ? 'Pedido recusado.' : 'Pedido voltou para análise.');
      setDecidir(null); carregar();
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'Não foi possível salvar.'); }
    setOcupado(false);
  };

  if (!dados) return projetoId ? <div style={{ fontSize: '0.8rem', color: 'var(--texto-suave)', marginBottom: 14 }}>Carregando pedidos de retificação…</div> : null;
  // Na tela da sessao: so atas ainda nao votadas que tenham pedidos
  const atas = (dados.atas || []).filter(a => projetoId || (a.aberta && a.pedidos.length));
  if (!projetoId && !atas.length) return null;

  return (
    <div style={{ marginBottom: projetoId ? 16 : 24 }}>
      <div style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: projetoId ? '0.8rem' : '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, textTransform: projetoId ? 'uppercase' : 'none', letterSpacing: projetoId ? 1 : 0, color: projetoId ? 'var(--texto-suave)' : 'inherit' }}>
          <BookOpen size={16} /> Pedidos de retificação da ata
        </h2>
        <p style={{ fontSize: '0.72rem', color: 'var(--texto-suave)', marginTop: 2 }}>
          Os vereadores leem a ata no tablet e pedem correção. Ao aceitar, o texto entra sozinho na retificação que vai à votação.
        </p>
      </div>

      {dados.erro && <div className="card sem-dados"><p>Não foi possível buscar os pedidos.</p></div>}
      {atas.map(a => {
        const pend = a.pedidos.filter(p => p.status === 'PENDENTE').length;
        const aceitos = a.pedidos.filter(p => p.status === 'ACEITO');
        return (
          <div key={a.id} className="card" style={{ padding: '12px 16px', marginBottom: 10 }}>
            {!projetoId && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                <b style={{ fontSize: '0.9rem' }}>{tituloAta(a)}</b>
                {pend > 0 && <span className="badge badge-amarelo"><Clock size={9} /> {pend} aguardando</span>}
              </div>
            )}
            {!a.aberta && <p style={{ fontSize: '0.78rem', color: 'var(--texto-suave)', marginBottom: 6 }}>Ata já votada ({a.resultado === 'APROVADA' ? 'aprovada' : 'reprovada'}). Os pedidos ficam só para consulta.</p>}
            {a.emVotacao && <p style={{ fontSize: '0.78rem', color: 'var(--reprovado)', marginBottom: 6, fontWeight: 700 }}>A ata está em votação agora.</p>}
            {!a.pedidos.length && <p style={{ fontSize: '0.8rem', color: 'var(--texto-suave)' }}>Nenhum pedido de retificação{a.aberta ? ' até agora' : ''}.</p>}

            {a.pedidos.map(p => (
              <div key={p.id} style={{ borderTop: '1px solid var(--cinza)', padding: '10px 0' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <b style={{ fontSize: '0.85rem' }}>Ver. {p.vereadorNome}</b>
                  <span className={`badge ${(SIT[p.status] || SIT.PENDENTE)[1]}`}>{(SIT[p.status] || SIT.PENDENTE)[0]}</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--texto-suave)' }}>{horaBR(p.criadoEm)}</span>
                  {a.aberta && !a.emVotacao && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      {p.status === 'PENDENTE' ? (<>
                        <button className="btn btn-primary btn-sm" disabled={ocupado} onClick={() => setDecidir({ pedido: p, status: 'ACEITO', textoAceito: p.texto, resposta: '' })}><Check size={13} /> Aceitar</button>
                        <button className="btn btn-secondary btn-sm" disabled={ocupado} style={{ color: 'var(--reprovado)' }} onClick={() => setDecidir({ pedido: p, status: 'RECUSADO', textoAceito: '', resposta: '' })}><X size={13} /> Recusar</button>
                      </>) : (
                        <button className="btn btn-secondary btn-sm" disabled={ocupado} onClick={() => { if (window.confirm('Voltar este pedido para análise?' + (p.status === 'ACEITO' ? ' Ele sai da retificação da ata.' : ''))) salvar(p, 'PENDENTE'); }}><RotateCcw size={13} /> Desfazer</button>
                      )}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: '0.85rem', marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{p.texto}</p>
                {p.status === 'ACEITO' && p.textoAceito && p.textoAceito !== p.texto && <p style={{ fontSize: '0.78rem', marginTop: 4 }}><b>Na retificação:</b> {p.textoAceito}</p>}
                {p.resposta && <p style={{ fontSize: '0.78rem', marginTop: 4, color: 'var(--texto-suave)' }}><b>Resposta:</b> {p.resposta}{p.respondidoPor ? ` — ${p.respondidoPor}` : ''}</p>}
              </div>
            ))}

            {aceitos.length > 0 && (
              <div style={{ marginTop: 8, background: 'var(--cinza-claro)', border: '1px solid var(--cinza)', borderRadius: 8, padding: '8px 12px' }}>
                <p style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--texto-suave)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Retificação que vai à votação</p>
                {aceitos.map(p => <p key={p.id} style={{ fontSize: '0.8rem', margin: '2px 0' }}>▸ Ver. {p.vereadorNome}: {p.textoAceito || p.texto}</p>)}
              </div>
            )}
          </div>
        );
      })}

      {decidir && (
        <div className="modal-overlay" onClick={() => !ocupado && setDecidir(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h2 className="modal-title">{decidir.status === 'ACEITO' ? 'Aceitar pedido de retificação' : 'Recusar pedido de retificação'}</h2>
              <button className="btn-modal-close" onClick={() => setDecidir(null)}><X size={18} /></button>
            </div>
            <div>
              <p style={{ fontSize: '0.8rem', color: 'var(--texto-suave)', marginBottom: 4 }}>Pedido do Ver. {decidir.pedido.vereadorNome}:</p>
              <p style={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', background: 'var(--cinza-claro)', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>{decidir.pedido.texto}</p>
              {decidir.status === 'ACEITO' && (
                <div className="form-group">
                  <label className="form-label">Texto que entra na retificação (pode ajustar)</label>
                  <textarea className="form-input" rows={4} value={decidir.textoAceito} onChange={e => setDecidir(d => ({ ...d, textoAceito: e.target.value }))} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">{decidir.status === 'ACEITO' ? 'Recado ao vereador (opcional)' : 'Motivo da recusa (o vereador vê)'}</label>
                <textarea className="form-input" rows={3} value={decidir.resposta} onChange={e => setDecidir(d => ({ ...d, resposta: e.target.value }))} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDecidir(null)} disabled={ocupado}>Cancelar</button>
              <button className="btn btn-primary" disabled={ocupado || (decidir.status === 'ACEITO' && !decidir.textoAceito.trim())}
                onClick={() => salvar(decidir.pedido, decidir.status, { resposta: decidir.resposta, textoAceito: decidir.status === 'ACEITO' ? decidir.textoAceito.trim() : undefined })}>
                {decidir.status === 'ACEITO' ? <><Check size={14} /> Aceitar</> : <><X size={14} /> Recusar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
